import React from "react";
import { AlertTriangle, RotateCw, Home } from "lucide-react";

export default class ErrorBoundary extends React.Component {
  state = { err: null };

  static getDerivedStateFromError(err) {
    return { err };
  }

  componentDidCatch(err, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", err, info?.componentStack);
  }

  // Navigating away from a crashed page recovers without remounting the whole tree.
  componentDidUpdate(prevProps) {
    if (this.state.err && prevProps.resetKey !== this.props.resetKey) this.reset();
  }

  reset = () => this.setState({ err: null });

  render() {
    if (!this.state.err) return this.props.children;

    const msg = this.state.err?.message || String(this.state.err);
    return (
      <div
        data-testid="error-boundary-fallback"
        className="min-h-screen grid place-items-center bg-slate-50 px-4"
      >
        <div className="max-w-md w-full bg-white rounded-3xl border border-slate-200 shadow-xl p-8 text-center">
          <div className="h-14 w-14 rounded-full bg-red-50 text-red-600 grid place-items-center mx-auto">
            <AlertTriangle size={26} />
          </div>
          <h1
            className="mt-5 text-2xl font-extrabold text-[#0A2540]"
            style={{ fontFamily: "Poppins" }}
          >
            Something went wrong
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            We hit a snag loading this page. Your data is safe — try again or head back home.
          </p>
          {process.env.NODE_ENV !== "production" && (
            <pre className="mt-4 text-[11px] bg-slate-50 border border-slate-200 rounded-lg p-3 text-left text-red-700 overflow-auto max-h-40">
              {msg}
            </pre>
          )}
          <div className="mt-6 flex items-center justify-center gap-2">
            <button
              data-testid="error-boundary-retry"
              onClick={() => {
                this.reset();
                window.location.reload();
              }}
              className="inline-flex items-center gap-2 h-11 px-5 rounded-full adv-btn-primary text-white text-sm font-semibold"
            >
              <RotateCw size={14} /> Try again
            </button>
            <a
              data-testid="error-boundary-home"
              href="/"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-slate-200 hover:bg-slate-50 text-sm font-semibold text-[#0A2540]"
            >
              <Home size={14} /> Go home
            </a>
          </div>
        </div>
      </div>
    );
  }
}
